import { Injectable, inject } from '@angular/core';
import { APP_ENVIRONMENT } from '@umss/shared/core';

export type TestIdType =
  | 'shift-need'
  | 'shift-request'
  | 'shift'
  | 'shift-clock-in'
  | 'shift-clock-out'
  | 'timesheet'
  | 'invoice';

export interface TestDataBatch {
  ids: string[];
  savedAt: number;
  count: number;
  formattedTime?: string; // HH:mm:ss
}

export interface TestDataItem {
  batches: TestDataBatch[];
  totalCount: number;
  firstCreatedAt: number;
  lastUpdatedAt: number;
}

/**
 * Stores test IDs in localStorage as a per-idType batch history, scoped by
 * environment (same-origin only — no cross-subdomain sharing).
 */
@Injectable({ providedIn: 'root' })
export class TestDataStorageService {
  private readonly config = inject(APP_ENVIRONMENT);

  getEnvironment(): string {
    return (this.config['environmentName'] || 'unknown') as string;
  }

  private getStorageKey(idType: TestIdType): string {
    return `${this.getEnvironment()}_${idType}_data`;
  }

  saveIds(idType: TestIdType, ids: string[]): void {
    if (!ids || ids.length === 0) return;

    const existing = this.getFromLocalStorage(idType);
    const now = Date.now();

    const newBatch: TestDataBatch = {
      ids,
      savedAt: now,
      count: ids.length,
      formattedTime: this.formatTime(now)
    };

    const data: TestDataItem = existing
      ? {
          batches: [...existing.batches, newBatch],
          totalCount: existing.totalCount + ids.length,
          firstCreatedAt: existing.firstCreatedAt,
          lastUpdatedAt: now
        }
      : {
          batches: [newBatch],
          totalCount: ids.length,
          firstCreatedAt: now,
          lastUpdatedAt: now
        };

    this.setLocalStorageItem(this.getStorageKey(idType), JSON.stringify(data));
  }

  getBatches(idType: TestIdType): TestDataBatch[] {
    return this.getData(idType)?.batches ?? [];
  }

  getBatchesByDateRange(
    idType: TestIdType,
    startTime: number,
    endTime: number
  ): TestDataBatch[] {
    return this.getBatches(idType).filter(
      (b) => b.savedAt >= startTime && b.savedAt <= endTime
    );
  }

  getBatchesByDate(idType: TestIdType, dateStr: string): TestDataBatch[] {
    const start = new Date(dateStr).getTime();
    return this.getBatchesByDateRange(idType, start, start + 86400000 - 1);
  }

  getIds(idType: TestIdType): string[] {
    const data = this.getData(idType);
    if (!data) return [];
    const allIds = data.batches.flatMap((b) => b.ids);
    return Array.from(new Set(allIds));
  }

  getData(idType: TestIdType): TestDataItem | null {
    return this.getFromLocalStorage(idType);
  }

  getAllData(): Record<TestIdType, TestDataItem | null> {
    const types: TestIdType[] = [
      'shift-need',
      'shift-request',
      'shift',
      'shift-clock-in',
      'shift-clock-out',
      'timesheet',
      'invoice'
    ];
    const result = {} as Record<TestIdType, TestDataItem | null>;
    for (const type of types) {
      result[type] = this.getData(type);
    }
    return result;
  }

  clearIds(idType: TestIdType): void {
    this.removeLocalStorageItem(this.getStorageKey(idType));
  }

  clearBatch(idType: TestIdType, batchIndex: number): void {
    const data = this.getFromLocalStorage(idType);
    if (!data || batchIndex < 0 || batchIndex >= data.batches.length) return;

    const updatedBatches = data.batches.filter((_, i) => i !== batchIndex);

    if (updatedBatches.length === 0) {
      this.clearIds(idType);
      return;
    }

    const updated: TestDataItem = {
      batches: updatedBatches,
      totalCount: updatedBatches.reduce((s, b) => s + b.count, 0),
      firstCreatedAt: data.firstCreatedAt,
      lastUpdatedAt: Date.now()
    };

    this.setLocalStorageItem(
      this.getStorageKey(idType),
      JSON.stringify(updated)
    );
  }

  clearAll(): void {
    const types: TestIdType[] = [
      'shift-need',
      'shift-request',
      'shift',
      'shift-clock-in',
      'shift-clock-out',
      'timesheet',
      'invoice'
    ];
    for (const type of types) {
      this.clearIds(type);
    }
  }

  exportJson(): string {
    return JSON.stringify(
      {
        environment: this.getEnvironment(),
        exportedAt: new Date().toISOString(),
        data: this.getAllData()
      },
      null,
      2
    );
  }

  importJson(jsonString: string): boolean {
    try {
      const imported = JSON.parse(jsonString);

      if (!imported.data || typeof imported.data !== 'object') {
        console.error('Invalid JSON structure');
        return false;
      }

      const types: TestIdType[] = [
        'shift-need',
        'shift-request',
        'shift',
        'shift-clock-in',
        'shift-clock-out',
        'timesheet',
        'invoice'
      ];

      for (const type of types) {
        const item = imported.data[type];
        if (!item) continue;

        if (item.batches && Array.isArray(item.batches)) {
          const data: TestDataItem = {
            batches: item.batches,
            totalCount: item.totalCount || 0,
            firstCreatedAt: item.firstCreatedAt || Date.now(),
            lastUpdatedAt: Date.now()
          };
          this.setLocalStorageItem(
            this.getStorageKey(type),
            JSON.stringify(data)
          );
        } else if (Array.isArray(item.ids) && item.ids.length > 0) {
          this.saveIds(type, item.ids);
        }
      }

      return true;
    } catch (error) {
      console.error('Failed to import JSON', error);
      return false;
    }
  }

  // ========== Private Helpers ==========

  private getFromLocalStorage(idType: TestIdType): TestDataItem | null {
    const value = this.getLocalStorageItem(this.getStorageKey(idType));
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      console.error(`Failed to parse test data for ${idType}`);
      return null;
    }
  }

  private setLocalStorageItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      console.error(`Failed to save test data key: ${key}`);
    }
  }

  private getLocalStorageItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private removeLocalStorageItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      console.error(`Failed to remove test data key: ${key}`);
    }
  }

  private formatTime(timestamp: number): string {
    const d = new Date(timestamp);
    return [d.getHours(), d.getMinutes(), d.getSeconds()]
      .map((n) => String(n).padStart(2, '0'))
      .join(':');
  }
}
